import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { FixedSizeGrid as Grid } from 'react-window';
import axios from 'axios';
import { TextField, Typography, CircularProgress, Button, IconButton, Tooltip, Modal, Backdrop } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { SERVER_NAME } from './config';
import { FaList, FaThLarge, FaSearchPlus, FaSearchMinus, FaTimes } from 'react-icons/fa';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    backgroundColor: '#000000',
    color: '#ffffff',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: '10px 16px 6px',
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    margin: 0,
    padding: 0,
    color: '#ffffff',
    textShadow: '0 0 15px rgba(77, 121, 255, 0.8)',
    textAlign: 'left',
  },
  subtitle: {
    marginTop: '6px',
    color: '#cccccc',
  },
  searchRow: {
    padding: '10px 16px 12px',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  viewToggleIcon: {
    color: '#ffffff',
    border: '1px solid rgba(77,121,255,0.55)',
    borderRadius: 8,
    padding: 8,
  },
  viewToggleIconActive: {
    color: '#4d79ff',
    border: '1px solid rgba(77,121,255,0.95)',
    borderRadius: 8,
    padding: 8,
  },
  searchField: {
    flex: 1,
    '& .MuiInputBase-input': {
      color: '#ffffff',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#4d79ff',
      },
      '&:hover fieldset': {
        borderColor: '#6b8fff',
      },
    },
  },
  listWrap: {
    flex: 1,
    minHeight: 0,
    borderTop: '1px solid rgba(77,121,255,0.35)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    cursor: 'pointer',
  },
  actionsRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    marginTop: 2,
  },
  actionButton: {
    borderColor: 'rgba(77,121,255,0.55)',
    color: '#ffffff',
    textTransform: 'none',
  },
  thumb: {
    width: '160px',
    height: '90px',
    backgroundColor: '#000000',
    border: '1px solid rgba(77,121,255,0.45)',
    borderRadius: '10px',
    overflow: 'hidden',
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  card: {
    padding: 10,
    boxSizing: 'border-box',
  },
  cardInner: {
    height: '100%',
    border: '1px solid rgba(77,121,255,0.35)',
    borderRadius: 12,
    padding: 10,
    background: 'rgba(0,0,0,0.65)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    cursor: 'pointer',
  },
  gridThumb: {
    width: '100%',
    height: 160,
    border: '1px solid rgba(77,121,255,0.25)',
    borderRadius: 10,
    overflow: 'hidden',
    background: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridThumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    position: 'relative',
    outline: 'none',
    maxWidth: '95vw',
    maxHeight: '95vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  modalImage: {
    maxWidth: '100%',
    maxHeight: 'calc(95vh - 60px)',
    objectFit: 'contain',
    transition: 'transform 0.2s ease',
    cursor: 'grab',
  },
  modalControls: {
    display: 'flex',
    gap: 12,
    padding: '12px 0',
    alignItems: 'center',
  },
  modalButton: {
    color: '#ffffff',
    backgroundColor: 'rgba(77,121,255,0.3)',
    border: '1px solid rgba(77,121,255,0.6)',
    borderRadius: 8,
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    '&:hover': {
      backgroundColor: 'rgba(77,121,255,0.5)',
    },
  },
  closeButton: {
    position: 'absolute',
    top: -40,
    right: 0,
    color: '#ffffff',
    backgroundColor: 'rgba(255,50,50,0.3)',
    border: '1px solid rgba(255,50,50,0.6)',
    borderRadius: '50%',
    padding: 8,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255,50,50,0.5)',
    },
  },
  zoomLevel: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 14,
    minWidth: 60,
    textAlign: 'center',
  },
}));

function debounce(fn, delay) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

export default function TimeseriesGallery() {
  const classes = useStyles();
  const navigate = useNavigate();

  const TIMESERIES_PNG_BASE = 'https://e3d.ai/timeseries_snapshots/png';
  const TIMESERIES_JSON_BASE = 'https://e3d.ai/timeseries_snapshots/json';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  
  // Zoom modal state
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [zoomItem, setZoomItem] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const queryRef = useRef('');
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);

  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? window.localStorage.getItem('timeseriesViewMode') : '';
      return v === 'list' ? 'list' : 'grid';
    } catch (e) {
      return 'grid';
    }
  });

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem('timeseriesViewMode', viewMode);
    } catch (e) {
    }
  }, [viewMode]);

  const limit = 60;

  const formatBucketLabel = useCallback((it) => {
    try {
      const iso = it && it.bucket_ts_utc_iso ? String(it.bucket_ts_utc_iso) : '';
      if (iso) {
        const d = new Date(iso);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          });
        }
      }

      const unix = it && it.bucket_ts_unix != null ? Number(it.bucket_ts_unix) : NaN;
      if (Number.isFinite(unix)) {
        const d2 = new Date(unix * 1000);
        if (!Number.isNaN(d2.getTime())) {
          return d2.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          });
        }
      }

      const raw = it && it.bucket_ts ? String(it.bucket_ts) : '';
      if (raw && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
        const d3 = new Date(raw.replace(' ', 'T') + 'Z');
        if (!Number.isNaN(d3.getTime())) {
          return d3.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          });
        }
      }

      return raw;
    } catch (e) {
      return it && it.bucket_ts ? String(it.bucket_ts) : '';
    }
  }, []);

  const fetchTimeseries = useCallback(async ({ q, limit, offset }) => {
    const primaryUrl = `${SERVER_NAME}/timeseries`;
    const fallbackUrl = `/api/timeseries`;

    const params = {
      q,
      limit,
      offset,
    };

    try {
      return await axios.get(primaryUrl, {
        params,
        withCredentials: false,
      });
    } catch (e1) {
      try {
        return await axios.get(fallbackUrl, {
          params,
          withCredentials: false,
        });
      } catch (e2) {
        throw e1;
      }
    }
  }, []);

  const buildUrls = useCallback((it) => {
    const snapKey = it && it.snapshot_key ? String(it.snapshot_key) : '';
    const png = it && it.png_filename ? String(it.png_filename) : '';
    const thumb = it && it.thumbnail_filename ? String(it.thumbnail_filename) : '';
    const json = it && it.json_filename ? String(it.json_filename) : '';

    const bust = `_t=${encodeURIComponent(String(Date.now()))}`;
    const thumbUrl = thumb ? `${TIMESERIES_PNG_BASE}/${encodeURIComponent(thumb)}?${bust}` : '';
    const pngUrl = png ? `${TIMESERIES_PNG_BASE}/${encodeURIComponent(png)}?${bust}` : '';
    const jsonUrl = json ? `${TIMESERIES_JSON_BASE}/${encodeURIComponent(json)}?${bust}` : '';

    return { snapKey, thumbUrl, pngUrl, jsonUrl };
  }, [TIMESERIES_PNG_BASE, TIMESERIES_JSON_BASE]);

  const handleOpen = useCallback((it, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    const { jsonUrl } = buildUrls(it);
    if (!jsonUrl) return;
    try {
      navigate(`/links?snapshot=${encodeURIComponent(jsonUrl)}`);
    } catch (e2) {
      try { window.location.href = `/links?snapshot=${encodeURIComponent(jsonUrl)}`; } catch (e3) {}
    }
  }, [navigate, buildUrls]);

  const handleDownload = useCallback((url, filename, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!url) return;
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ? String(filename) : 'snapshot';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e2) {
      try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (e3) {}
    }
  }, []);

  const handleZoomOpen = useCallback((it, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setZoomItem(it);
    setZoomLevel(1);
    setZoomModalOpen(true);
  }, []);

  const handleZoomClose = useCallback(() => {
    setZoomModalOpen(false);
    setZoomItem(null);
    setZoomLevel(1);
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.25));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(1);
  }, []);

  const resetAndFetch = useCallback(async (q) => {
    const qq = String(q || '');
    queryRef.current = qq;
    offsetRef.current = 0;
    hasMoreRef.current = true;
    setItems([]);
    setTotal(0);
    setError('');

    setLoading(true);
    try {
      const resp = await fetchTimeseries({ q: qq, limit, offset: 0 });
      const data = resp && resp.data ? resp.data : {};
      const nextItems = Array.isArray(data.items) ? data.items : [];
      const nextTotal = data.total != null ? Number(data.total) : 0;
      setItems(nextItems);
      setTotal(Number.isFinite(nextTotal) ? nextTotal : 0);
      offsetRef.current = nextItems.length;
      hasMoreRef.current = nextItems.length < (Number.isFinite(nextTotal) ? nextTotal : nextItems.length);
    } catch (e) {
      const status = e && e.response && e.response.status != null ? String(e.response.status) : '';
      const msg = e && e.message ? String(e.message) : '';
      setError(`Failed to load timeseries${status ? ` (status=${status})` : ''}${msg ? `: ${msg}` : ''}`);
      setItems([]);
      setTotal(0);
      offsetRef.current = 0;
      hasMoreRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [fetchTimeseries]);

  const loadMore = useCallback(async () => {
    if (loading) return;
    if (!hasMoreRef.current) return;

    const q = queryRef.current;
    const offset = offsetRef.current;

    setLoading(true);
    try {
      const resp = await fetchTimeseries({ q, limit, offset });
      const data = resp && resp.data ? resp.data : {};
      const next = Array.isArray(data.items) ? data.items : [];
      const nextTotal = data.total != null ? Number(data.total) : total;

      setItems((prev) => {
        const merged = prev.concat(next);
        offsetRef.current = merged.length;
        hasMoreRef.current = merged.length < (Number.isFinite(nextTotal) ? nextTotal : merged.length);
        return merged;
      });

      if (Number.isFinite(nextTotal)) setTotal(nextTotal);
    } catch (e) {
      hasMoreRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [loading, total, fetchTimeseries]);

  const debouncedSearch = useMemo(() => debounce((q) => {
    resetAndFetch(q);
  }, 350), [resetAndFetch]);

  useEffect(() => {
    resetAndFetch('');
  }, [resetAndFetch]);

  const onItemsRendered = useCallback((info) => {
    try {
      const stop = info && info.visibleStopIndex != null ? Number(info.visibleStopIndex) : 0;
      if (!Number.isFinite(stop)) return;
      if (stop >= Math.max(0, items.length - 12)) {
        loadMore();
      }
    } catch (e) {
    }
  }, [items.length, loadMore]);

  const viewport = useMemo(() => {
    try {
      return { w: window.innerWidth || 1200, h: window.innerHeight || 800 };
    } catch (e) {
      return { w: 1200, h: 800 };
    }
  }, [viewMode]);

  const gridCols = useMemo(() => {
    const w = Number(viewport.w) || 0;
    if (w <= 0) return 2;
    if (w < 700) return 2;
    if (w < 1100) return 3;
    return 4;
  }, [viewport.w]);

  const gridCellWidth = useMemo(() => {
    const w = Number(viewport.w) || 1200;
    const gutter = 16;
    return Math.max(240, Math.floor((w - gutter * 2) / gridCols));
  }, [viewport.w, gridCols]);

  const gridCellHeight = 260;

  const renderListRow = useCallback(({ index, style }) => {
    const it = items[index];
    if (!it) return null;
    const { thumbUrl, pngUrl, jsonUrl } = buildUrls(it);
    const bucket = formatBucketLabel(it);
    const key = it && it.snapshot_key ? String(it.snapshot_key) : '';

    return (
      <div style={style} className={classes.row}>
        <div className={classes.thumb} onClick={(e) => handleZoomOpen(it, e)} style={{ cursor: 'zoom-in' }}>
          {thumbUrl ? (
            <img src={thumbUrl} alt={key} className={classes.thumbImg} />
          ) : null}
        </div>
        <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => handleOpen(it)}>
          <Typography variant="subtitle1" style={{ color: '#fff', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {bucket || key}
          </Typography>
          <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.65)' }}>
            {key}
          </Typography>
          <div className={classes.actionsRow}>
            <Button variant="outlined" className={classes.actionButton} onClick={(e) => handleOpen(it, e)}>
              Open
            </Button>
            <Button variant="outlined" className={classes.actionButton} onClick={(e) => handleDownload(pngUrl, (key || 'snapshot') + '.png', e)}>
              PNG
            </Button>
            <Button variant="outlined" className={classes.actionButton} onClick={(e) => handleDownload(jsonUrl, (key || 'snapshot') + '.json', e)}>
              JSON
            </Button>
          </div>
        </div>
      </div>
    );
  }, [items, buildUrls, classes.row, classes.thumb, classes.thumbImg, classes.actionsRow, classes.actionButton, handleOpen, handleDownload, handleZoomOpen, formatBucketLabel]);

  const renderGridCell = useCallback(({ columnIndex, rowIndex, style }) => {
    const idx = rowIndex * gridCols + columnIndex;
    const it = items[idx];
    if (!it) return null;
    const { thumbUrl, pngUrl, jsonUrl } = buildUrls(it);
    const bucket = formatBucketLabel(it);
    const key = it && it.snapshot_key ? String(it.snapshot_key) : '';

    return (
      <div style={style} className={classes.card}>
        <div className={classes.cardInner}>
          <div className={classes.gridThumb} onClick={(e) => handleZoomOpen(it, e)} style={{ cursor: 'zoom-in' }}>
            {thumbUrl ? (
              <img src={thumbUrl} alt={key} className={classes.gridThumbImg} />
            ) : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer' }} onClick={() => handleOpen(it)}>
            <Typography variant="subtitle2" style={{ color: '#fff', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {bucket || key}
            </Typography>
            <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.65)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {key}
            </Typography>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" className={classes.actionButton} onClick={(e) => handleOpen(it, e)}>
              Open
            </Button>
            <Button size="small" variant="outlined" className={classes.actionButton} onClick={(e) => handleDownload(pngUrl, (key || 'snapshot') + '.png', e)}>
              PNG
            </Button>
            <Button size="small" variant="outlined" className={classes.actionButton} onClick={(e) => handleDownload(jsonUrl, (key || 'snapshot') + '.json', e)}>
              JSON
            </Button>
          </div>
        </div>
      </div>
    );
  }, [items, gridCols, buildUrls, classes.card, classes.cardInner, classes.gridThumb, classes.gridThumbImg, classes.actionButton, handleOpen, handleDownload, handleZoomOpen, formatBucketLabel]);

  const gridRowCount = useMemo(() => {
    return Math.ceil(items.length / gridCols);
  }, [items.length, gridCols]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography className={classes.title}>Blockchain Transaction Graph Gallery</Typography>
      </div>

      <div className={classes.searchRow}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search snapshot key"
          className={classes.searchField}
          value={query}
          onChange={(e) => {
            const v = e && e.target ? e.target.value : '';
            setQuery(v);
            debouncedSearch(v);
          }}
        />
        <Tooltip title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}>
          <IconButton
            onClick={() => setViewMode((prev) => (prev === 'grid' ? 'list' : 'grid'))}
            className={viewMode === 'grid' ? classes.viewToggleIconActive : classes.viewToggleIcon}
            size="small"
          >
            {viewMode === 'grid' ? <FaList /> : <FaThLarge />}
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.listWrap}>
        {error ? (
          <div style={{ padding: 16 }}>{error}</div>
        ) : null}

        {loading && !items.length ? (
          <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <CircularProgress size={16} style={{ color: '#4d79ff' }} />
            <span>Loading...</span>
          </div>
        ) : null}

        {!loading && !items.length && !error ? (
          <div style={{ padding: 16 }}>No snapshots yet.</div>
        ) : null}

        {items.length > 0 && viewMode === 'list' ? (
          <List
            height={Math.max(200, (viewport.h || 800) - 220)}
            width={viewport.w || 1200}
            itemCount={items.length}
            itemSize={116}
            onItemsRendered={onItemsRendered}
          >
            {renderListRow}
          </List>
        ) : null}

        {items.length > 0 && viewMode === 'grid' ? (
          <Grid
            height={Math.max(200, (viewport.h || 800) - 220)}
            width={viewport.w || 1200}
            columnCount={gridCols}
            columnWidth={gridCellWidth}
            rowCount={gridRowCount}
            rowHeight={gridCellHeight}
            onItemsRendered={onItemsRendered}
          >
            {renderGridCell}
          </Grid>
        ) : null}

        {items.length > 0 ? (
          <div style={{ padding: 12, color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
            Showing {items.length} of {total}
          </div>
        ) : null}
      </div>

      {/* Zoom Modal */}
      <Modal
        open={zoomModalOpen}
        onClose={handleZoomClose}
        className={classes.modal}
        BackdropComponent={Backdrop}
        BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.9)' } }}
      >
        <div className={classes.modalContent}>
          <IconButton className={classes.closeButton} onClick={handleZoomClose}>
            <FaTimes />
          </IconButton>
          {zoomItem ? (() => {
            const { pngUrl } = buildUrls(zoomItem);
            return (
              <>
                <img
                  src={pngUrl}
                  alt={zoomItem.snapshot_key || 'snapshot'}
                  className={classes.modalImage}
                  style={{ transform: `scale(${zoomLevel})` }}
                />
                <div className={classes.modalControls}>
                  <button className={classes.modalButton} onClick={handleZoomOut}>
                    <FaSearchMinus /> Zoom Out
                  </button>
                  <span className={classes.zoomLevel}>{Math.round(zoomLevel * 100)}%</span>
                  <button className={classes.modalButton} onClick={handleZoomIn}>
                    <FaSearchPlus /> Zoom In
                  </button>
                  <button className={classes.modalButton} onClick={handleZoomReset}>
                    Reset
                  </button>
                  <button className={classes.modalButton} onClick={() => handleOpen(zoomItem)}>
                    Open in Graph View
                  </button>
                </div>
              </>
            );
          })() : null}
        </div>
      </Modal>
    </div>
  );
}
